import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  FileText, Plus, Trash2, ShoppingCart, Edit2,
  Save, X, Package, Download, FileDown
} from 'lucide-react'
import TopHeader from '../components/layout/TopHeader.jsx'
import { templateService, productService, cartService } from '../services/api.js'
import { useCartStore } from '../store/index.js'
import toast from 'react-hot-toast'

const fmtDate = (d) => new Date(d).toLocaleDateString('en-ZA', {
  day: 'numeric', month: 'short', year: 'numeric',
})

// ── CSV export ────────────────────────────────────────────────────────────────
function exportCsv(tmpl, rows) {
  const headers = ['Description', 'Quantity', 'Note']
  const csvRows = rows.map(r =>
    [`"${(r.description || r.product_name || '').replace(/"/g, '""')}"`, r.quantity, r.note || ''].join(',')
  )
  const csv = [headers.join(','), ...csvRows].join('\n')
  const blob = new Blob([csv], { type: 'text/csv' })
  const url  = URL.createObjectURL(blob)
  const a    = document.createElement('a')
  a.href     = url
  a.download = `${tmpl.name.replace(/[^a-z0-9]/gi, '_')}_template.csv`
  a.click()
  URL.revokeObjectURL(url)
  toast.success('CSV downloaded')
}

// ── PDF export (simple print-friendly HTML → blob) ────────────────────────────
function exportPdf(tmpl, rows) {
  const rowsHtml = rows.map((r, i) => `
    <tr style="background:${i % 2 === 0 ? '#f9fafb' : '#fff'}">
      <td style="padding:8px 12px;border-bottom:1px solid #e5e7eb">${r.description || r.product_name || ''}</td>
      <td style="padding:8px 12px;border-bottom:1px solid #e5e7eb;text-align:center">${r.quantity}</td>
      <td style="padding:8px 12px;border-bottom:1px solid #e5e7eb;color:#6b7280">${r.note || ''}</td>
    </tr>`).join('')

  const html = `<!DOCTYPE html><html><head><meta charset="utf-8">
    <title>${tmpl.name}</title>
    <style>body{font-family:Arial,sans-serif;margin:32px;color:#111}
    h1{color:#0e947a;margin-bottom:4px}p{color:#6b7280;margin:0 0 24px}
    table{width:100%;border-collapse:collapse}
    th{background:#0e947a;color:#fff;padding:10px 12px;text-align:left}
    @media print{body{margin:16px}}</style></head><body>
    <h1>${tmpl.name}</h1>
    <p>${tmpl.description || ''} &nbsp;·&nbsp; ${rows.length} item${rows.length !== 1 ? 's' : ''} &nbsp;·&nbsp; ${fmtDate(new Date())}</p>
    <table><thead><tr>
      <th>Description</th><th style="text-align:center">Qty</th><th>Note</th>
    </tr></thead><tbody>${rowsHtml}</tbody></table>
    <script>window.onload=()=>{window.print()}<\/script></body></html>`

  const blob = new Blob([html], { type: 'text/html' })
  const url  = URL.createObjectURL(blob)
  window.open(url, '_blank')
  setTimeout(() => URL.revokeObjectURL(url), 10000)
}

// ── Create Template Modal ─────────────────────────────────────────────────────
function CreateSheet({ onClose, onCreate }) {
  const [name, setName] = useState('')
  const [desc, setDesc] = useState('')
  const [busy, setBusy] = useState(false)

  const handleCreate = async () => {
    if (!name.trim()) { toast.error('Template name required'); return }
    setBusy(true)
    try { await onCreate({ name: name.trim(), description: desc.trim() }); onClose() }
    finally { setBusy(false) }
  }

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-end" onClick={onClose}>
      <div className="w-full bg-surface-card rounded-t-3xl p-6 space-y-4" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="font-heading text-white text-lg">New Template</h3>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-surface-elevated flex items-center justify-center">
            <X size={14} className="text-gray-400" />
          </button>
        </div>
        <div>
          <label className="block text-gray-400 text-xs font-display mb-1.5">Template Name *</label>
          <input className="input-field" placeholder="e.g. Weekly Bar Order"
            value={name} onChange={e => setName(e.target.value)} autoFocus />
        </div>
        <div>
          <label className="block text-gray-400 text-xs font-display mb-1.5">Description (optional)</label>
          <input className="input-field" placeholder="Brief note about this template"
            value={desc} onChange={e => setDesc(e.target.value)} />
        </div>
        <button onClick={handleCreate} disabled={busy || !name.trim()}
          className="btn-primary w-full py-3.5 flex items-center justify-center gap-2 disabled:opacity-50">
          {busy ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            : <><Plus size={15} /> Create Template</>}
        </button>
      </div>
    </div>
  )
}

// ── Template Editor ───────────────────────────────────────────────────────────
function TemplateEditor({ templateId, templateName, onBack }) {
  const qc = useQueryClient()
  const [rows, setRows]       = useState(null)
  const [dirty, setDirty]     = useState(false)
  const [saving, setSaving]   = useState(false)
  const [search, setSearch]   = useState('')
  const [searchRes, setSearchRes] = useState([])
  const [searching, setSearching] = useState(false)

  // Load template items
  const { data: tmpl, isLoading } = useQuery({
    queryKey: ['template', templateId],
    queryFn:  () => templateService.getById(templateId),
  })

  // Initialise rows once from server data
  if (!isLoading && tmpl && rows === null) {
    setRows((tmpl.items || []).map(i => ({
      stock_code:   i.stock_code || i.product?.stock_code || '',
      product_name: i.product?.description || i.product_name || '',
      description:  i.description || i.product?.description || i.product_name || '',
      quantity:     i.quantity || 1,
      note:         i.note || '',
    })))
  }

  const mergeServerCart = useCartStore(s => s.mergeServerCart)
  const zeroAllQuantities = useCartStore(s => s.zeroAllQuantities)
  const loadToCartMut = useMutation({
    mutationFn: () => templateService.loadToCart(templateId),
    onSuccess: async () => {
      try {
        const cart = await cartService.get()
        if (cart?.items) mergeServerCart(cart.items)
      } catch { }
      zeroAllQuantities()
      toast.success('Template loaded — enter your quantities 🛒')
      onBack()
    },
    onError: (e) => toast.error(e.message || 'Failed to load to cart'),
  })

  const handleSearchProduct = async (q) => {
    setSearch(q)
    if (q.length < 2) { setSearchRes([]); return }
    setSearching(true)
    try {
      const data = await productService.getAll({ search: q, limit: 10 })
      setSearchRes(Array.isArray(data) ? data : (data?.items ?? []))
    } catch { setSearchRes([]) }
    finally { setSearching(false) }
  }

  const addProduct = (product) => {
    const existing = rows.find(r => r.stock_code === product.stock_code)
    if (existing) {
      setRows(prev => prev.map(r =>
        r.stock_code === product.stock_code ? { ...r, quantity: r.quantity + 1 } : r
      ))
    } else {
      setRows(prev => [...prev, {
        stock_code: product.stock_code,
        product_name: product.description || product.stock_code,
        quantity: 1, note: '',
      }])
    }
    setSearch(''); setSearchRes([]); setDirty(true)
    toast.success(`Added ${product.description || product.stock_code}`, { duration: 1500 })
  }

  const addBlankRow = () => {
    setRows(prev => [...prev, { stock_code: '', product_name: '', description: '', quantity: 1, note: '' }])
    setDirty(true)
  }

  const updateRow = (idx, field, value) => {
    setRows(prev => prev.map((r, i) => i === idx ? { ...r, [field]: value } : r))
    setDirty(true)
  }

  const removeRow = (idx) => {
    setRows(prev => prev.filter((_, i) => i !== idx)); setDirty(true)
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      await templateService.saveItems(templateId, rows.map(r => ({
        stock_code: r.stock_code,
        quantity: Number(r.quantity) || 1,
        note: r.note || '',
      })))
      qc.invalidateQueries(['template', templateId])
      qc.invalidateQueries(['templates'])
      setDirty(false)
      toast.success('Template saved!')
    } catch (e) { toast.error(e.message || 'Save failed') }
    finally { setSaving(false) }
  }

  const currentTmpl = tmpl || { name: templateName, description: '' }

  if (isLoading || rows === null) return (
    <div className="page-container">
      <div className="fixed top-0 left-0 right-0 z-40 bg-surface-dark/95 backdrop-blur-md border-b border-surface-border flex items-center gap-3 px-4 h-14" style={{ paddingTop: 'var(--safe-top)' }}>
        <button onClick={onBack} className="w-9 h-9 rounded-xl bg-surface-elevated flex items-center justify-center">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M12 5l-7 7 7 7"/></svg>
        </button>
        <span className="font-heading text-white text-lg">Loading…</span>
      </div>
      <div className="flex justify-center pt-20"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>
    </div>
  )

  return (
    <div className="page-container bg-surface-dark">
      {/* Header */}
      <div className="fixed top-0 left-0 right-0 z-40 bg-surface-dark/95 backdrop-blur-md border-b border-surface-border" style={{ paddingTop: 'var(--safe-top)' }}>
        <div className="flex items-center gap-3 px-4 h-14">
          <button onClick={onBack} className="w-9 h-9 rounded-xl bg-surface-elevated flex items-center justify-center">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M12 5l-7 7 7 7"/></svg>
          </button>
          <div className="flex-1 min-w-0">
            <p className="font-heading text-white text-base truncate">{currentTmpl.name}</p>
            <p className="text-gray-500 text-xs font-display">{(rows || []).length} items</p>
          </div>
          {/* Export buttons */}
          <button onClick={() => exportCsv(currentTmpl, rows || [])}
            className="w-9 h-9 rounded-xl bg-surface-elevated flex items-center justify-center" title="Download CSV">
            <Download size={16} className="text-gray-300" />
          </button>
          <button onClick={() => exportPdf(currentTmpl, rows || [])}
            className="w-9 h-9 rounded-xl bg-surface-elevated flex items-center justify-center" title="Print / PDF">
            <FileDown size={16} className="text-gray-300" />
          </button>
          {/* Always-visible save button */}
          <button onClick={handleSave} disabled={saving}
            className={`flex items-center gap-1.5 rounded-xl px-3 py-2 text-xs font-display font-semibold disabled:opacity-60 transition-colors ${
              dirty ? 'bg-primary text-white' : 'bg-surface-elevated text-gray-400'
            }`}>
            {saving ? <div className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Save size={13} />}
            Save
          </button>
        </div>
      </div>

      <div className="px-4 pt-3 pb-36 space-y-3">
        {/* Product search */}
        <div className="relative">
          <input value={search} onChange={e => handleSearchProduct(e.target.value)}
            placeholder="Search & add products by name or stock code…"
            className="input-field pr-10 text-sm" />
          {search && (
            <button onClick={() => { setSearch(''); setSearchRes([]) }}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">
              <X size={14} />
            </button>
          )}
          {(searchRes.length > 0 || searching) && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-surface-card border border-surface-border rounded-xl shadow-xl z-30 overflow-hidden max-h-56 overflow-y-auto">
              {searching && <div className="flex justify-center py-3"><div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>}
              {searchRes.map(p => (
                <button key={p.id} onClick={() => addProduct(p)}
                  className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-surface-elevated transition-colors border-b border-surface-border/50 last:border-0">
                  <Package size={14} className="text-primary flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm font-display truncate">{p.description || p.stock_code}</p>
                    <p className="text-gray-500 text-xs font-mono">{p.stock_code}</p>
                  </div>
                  <span className="text-gold text-xs font-display flex-shrink-0">R{(p.inclprice ?? 0).toFixed(2)}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Add blank row button */}
        <button onClick={addBlankRow}
          className="w-full flex items-center justify-center gap-2 py-2 rounded-xl border border-dashed border-surface-border text-gray-500 text-xs font-display active:border-primary active:text-primary transition-colors">
          <Plus size={13} /> Add row manually
        </button>

        {/* Rows */}
        {(rows || []).length === 0 ? (
          <div className="text-center py-10">
            <FileText size={32} className="text-gray-600 mx-auto mb-3" />
            <p className="text-gray-400 font-display text-sm">No items yet — search above or add a blank row</p>
          </div>
        ) : (
          (rows || []).map((row, idx) => (
            <div key={idx} className="bg-surface-card rounded-2xl border border-surface-border p-3 space-y-2">
              {/* Description row */}
              <div className="flex items-center gap-2">
                <input
                  value={row.product_name}
                  onChange={e => updateRow(idx, 'product_name', e.target.value)}
                  placeholder="Product description"
                  className="input-field text-xs py-1.5 flex-1"
                />
                <button onClick={() => removeRow(idx)}
                  className="w-8 h-8 rounded-lg bg-red-900/20 flex items-center justify-center flex-shrink-0">
                  <Trash2 size={13} className="text-red-400" />
                </button>
              </div>
              {/* Qty + Note */}
              <div className="flex items-center gap-2">
                <div className="flex items-center bg-surface-elevated rounded-xl overflow-hidden border border-surface-border flex-shrink-0">
                  <button onClick={() => updateRow(idx, 'quantity', Math.max(1, row.quantity - 1))}
                    className="px-3 py-1.5 text-gray-300 active:bg-surface-border text-lg leading-none">−</button>
                  <span className="px-2 text-white font-display font-semibold text-sm min-w-[2rem] text-center">{row.quantity}</span>
                  <button onClick={() => updateRow(idx, 'quantity', row.quantity + 1)}
                    className="px-3 py-1.5 text-gray-300 active:bg-surface-border text-lg leading-none">+</button>
                </div>
                <input value={row.note} onChange={e => updateRow(idx, 'note', e.target.value)}
                  placeholder="Note (optional)"
                  className="flex-1 bg-surface-elevated border border-surface-border rounded-xl px-3 py-1.5 text-sm text-gray-300 placeholder-gray-600 font-display focus:outline-none focus:border-primary" />
              </div>
            </div>
          ))
        )}
      </div>

      {/* Fixed footer — Save always visible */}
      <div className="fixed bottom-0 left-0 right-0 z-50 bg-surface-card border-t border-surface-border px-4 pt-3 space-y-2"
        style={{ paddingBottom: 'max(0.75rem,var(--safe-bottom))' }}>
        <button onClick={handleSave} disabled={saving}
          className={`w-full py-3 flex items-center justify-center gap-2 text-sm rounded-xl font-display font-semibold border transition-colors ${
            dirty
              ? 'bg-primary text-white border-primary'
              : 'bg-surface-elevated text-gray-400 border-surface-border'
          }`}>
          {saving ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Save size={15} />}
          {dirty ? 'Save Changes' : 'Saved'}
        </button>
        <button onClick={() => loadToCartMut.mutate()}
          disabled={loadToCartMut.isPending || (rows || []).length === 0}
          className="btn-primary w-full py-4 flex items-center justify-center gap-2 disabled:opacity-50">
          {loadToCartMut.isPending
            ? <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            : <><ShoppingCart size={18} /> Load to Cart</>}
        </button>
      </div>
    </div>
  )
}

// ── Template List ─────────────────────────────────────────────────────────────
export default function Templates() {
  const navigate = useNavigate()
  const qc       = useQueryClient()
  const [creating, setCreating]   = useState(false)
  const [editingId, setEditingId] = useState(null)
  const [editingName, setEditingName] = useState('')

  const { data, isLoading } = useQuery({
    queryKey: ['templates'],
    queryFn: templateService.getAll,
    staleTime: 2 * 60 * 1000,
  })

  const createMut = useMutation({
    mutationFn: templateService.create,
    onSuccess: (tmpl) => {
      qc.invalidateQueries(['templates'])
      toast.success('Template created')
      setEditingId(tmpl.id)
      setEditingName(tmpl.name)
    },
    onError: () => toast.error('Failed to create template'),
  })

  const deleteMut = useMutation({
    mutationFn: templateService.delete,
    onSuccess: () => { qc.invalidateQueries(['templates']); toast.success('Template deleted') },
    onError: () => toast.error('Failed to delete template'),
  })

  const mergeServerCart = useCartStore(s => s.mergeServerCart)
  const zeroAllQuantities = useCartStore(s => s.zeroAllQuantities)
  const loadToCartMut = useMutation({
    mutationFn: (id) => templateService.loadToCart(id),
    onSuccess: async () => {
      try {
        const cart = await cartService.get()
        if (cart?.items) mergeServerCart(cart.items)
      } catch { }
      zeroAllQuantities()
      toast.success('Template loaded — enter your quantities 🛒')
      navigate('/cart')
    },
    onError: (e) => toast.error(e.message || 'Failed to load to cart'),
  })

  if (editingId) return (
    <TemplateEditor
      templateId={editingId}
      templateName={editingName}
      onBack={() => { setEditingId(null); setEditingName('') }}
    />
  )

  const list = Array.isArray(data) ? data : []

  return (
    <div className="page-container bg-surface-dark">
      <TopHeader title="Order Templates" showBack backTo="/account" />

      <div className="mx-4 mt-2 mb-4 bg-gradient-to-r from-primary-dark to-primary rounded-2xl p-4 flex items-center gap-4">
        <span className="text-4xl">📋</span>
        <div className="flex-1">
          <p className="font-heading text-white text-base">Reorder Made Easy</p>
          <p className="font-display text-gray-200 text-sm">Save your regular orders as templates</p>
        </div>
        <button onClick={() => setCreating(true)}
          className="btn-gold py-2 px-3 text-xs whitespace-nowrap flex items-center gap-1">
          <Plus size={13} /> New
        </button>
      </div>

      <div className="px-4 pb-8 space-y-3">
        {isLoading ? (
          Array(3).fill(0).map((_, i) => (
            <div key={i} className="bg-surface-card rounded-2xl border border-surface-border h-24 animate-pulse" />
          ))
        ) : list.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <span className="text-5xl mb-4">📋</span>
            <p className="text-white font-heading text-lg mb-1">No templates yet</p>
            <p className="text-gray-400 font-display text-sm mb-5">Save your regular orders to reorder quickly</p>
            <button onClick={() => setCreating(true)} className="btn-primary py-3 px-6 flex items-center gap-2">
              <Plus size={16} /> Create First Template
            </button>
          </div>
        ) : (
          list.map(tmpl => (
            <div key={tmpl.id} className="bg-surface-card rounded-2xl border border-surface-border overflow-hidden">
              <div className="p-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-primary/20 flex items-center justify-center flex-shrink-0">
                    <FileText size={18} className="text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-heading text-white text-base leading-tight">{tmpl.name}</p>
                    {tmpl.description && (
                      <p className="text-gray-400 text-xs font-display mt-0.5 line-clamp-1">{tmpl.description}</p>
                    )}
                    <div className="flex items-center gap-3 mt-1">
                      <span className="text-gray-500 text-xs font-display">
                        {tmpl.item_count ?? (tmpl.items?.length ?? 0)} items
                      </span>
                      {tmpl.updated_at && (
                        <span className="text-gray-600 text-xs font-display">· {fmtDate(tmpl.updated_at)}</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex border-t border-surface-border divide-x divide-surface-border">
                <button onClick={() => { setEditingId(tmpl.id); setEditingName(tmpl.name) }}
                  className="flex-1 flex items-center justify-center gap-1.5 py-3 text-gray-300 text-xs font-display active:bg-surface-elevated">
                  <Edit2 size={13} /> Edit
                </button>
                <button onClick={() => loadToCartMut.mutate(tmpl.id)} disabled={loadToCartMut.isPending}
                  className="flex-1 flex items-center justify-center gap-1.5 py-3 text-primary text-xs font-display font-semibold active:bg-primary/10 disabled:opacity-50">
                  <ShoppingCart size={13} /> Load to Cart
                </button>
                <button onClick={() => deleteMut.mutate(tmpl.id)}
                  className="flex items-center justify-center px-4 py-3 text-red-400 active:bg-red-900/20">
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {creating && (
        <CreateSheet onClose={() => setCreating(false)} onCreate={createMut.mutateAsync} />
      )}
    </div>
  )
}
